import { TFunction } from 'i18next';
import * as yup from 'yup';

import { RoomAssignType } from 'components/BreakoutRooms/utils';
import constants, { CUSTOM_ERROR_NAMES } from 'Constants';
import i18n from 'i18n/config';
import { checkForDateInFuture } from 'utils/utils';

import './schemaExtensions';
import { getCreateNewRoom } from './getCreateNewRoom/getCreateNewRoom';

export const FEEDBACK_MAX_CHARS = 2048;
const MAX_DISPLAY_NAME_CHARS = 255;

export const getAliasValidation = () =>
  yup
    .string()
    .required()
    .min(3)
    .max(50)
    .matches(/^[a-z0-9-]+$/i)
    .trim();

const getCreateRoom = (t: TFunction, isAliasSupported: boolean) =>
  yup
    .object()
    .shape({
      roomTitle: yup.string().required().min(3).max(50).trim(),
      isWaitingListEnabled: yup.boolean().required(),
      roomAlias: isAliasSupported ? getAliasValidation() : yup.string().nullable().optional(),
      startDate: yup.date().nullable().optional(),
      startTime: yup.date().nullable().optional(),
      description: yup.string().max(constants.MAX_TOPIC_LENGTH).optional().trim(),
    })
    .test(CUSTOM_ERROR_NAMES.FUTURE_DATE, t('future_date_error'), ({ startDate, startTime }) =>
      checkForDateInFuture({ startDate, startTime })
    );

const getJoinRoom = () =>
  yup.object().shape({
    name: yup.string().required().max(MAX_DISPLAY_NAME_CHARS).trim(),
    acceptedToS: yup.boolean().required().isTrue(),
  });

const getJoinMatrixRoom = (t: TFunction) =>
  yup.object().shape({
    roomId: yup
      .string()
      .matches(/^![A-Za-z0-9_-]+:[A-Za-z0-9.-]+$/, t('invalid_room_id'))
      .required(),
  });

const getMatrixHomeserver = () =>
  yup.object().shape({
    homeserver: yup.string().required().max(100),
  });

const getMessageValidation = () =>
  yup
    .object({
      message: yup.string().optional().max(2000).trim(),
      files: yup.mixed().optional(),
    })
    .test('one-of-two', '', ({ message, files }) => (message || files ? true : false));

const getFeedbackValidation = () =>
  yup.object().shape({ feedbackComment: yup.string().required().max(FEEDBACK_MAX_CHARS, i18n.t('feedback.too_long')) });

const getUpdateDisplayName = () =>
  yup.object().shape({
    displayName: yup
      .string()
      .required(i18n.t('display_name_required'))
      .max(MAX_DISPLAY_NAME_CHARS, i18n.t('display_name_too_long', { maxLength: MAX_DISPLAY_NAME_CHARS }))
      .trim(),
  });

const getCreateBreakoutRoom = () =>
  yup.object().shape({
    roomCount: yup.number().required(),
    assignment: yup.mixed<RoomAssignType>().oneOf(Object.values(RoomAssignType)).required(),
    allowReturnToMain: yup.boolean().required(),
    timeLimit: yup.date().nullable().optional(),
  });

const getRenameBreakoutRoom = () =>
  yup.object().shape({
    roomTitle: yup.string().max(100).required().trim(),
  });

const getBreakoutRoomSettings = (t: TFunction) =>
  yup.object().shape({
    allowReturnToMain: yup.boolean(),
    timeLimitHours: yup.number(),
    timeLimitMinutes: yup.number(),
    displayTimeLeft: yup.boolean(),
    isTimeLimitActive: yup
      .boolean()
      .test('isTimeLimitActive', t('breakout_rooms.time_limit_error'), function (isActive) {
        if (!isActive) return true;

        const { timeLimitHours, timeLimitMinutes } = this.parent;
        if (!timeLimitHours && !timeLimitMinutes) {
          return this.createError({
            path: this.path,
            message: t('breakout_rooms.time_limit_error'),
          });
        }

        return true;
      }),
  });

export const Schemas = {
  getCreateRoom,
  getJoinRoom,
  getJoinMatrixRoom,
  getMatrixHomeserver,
  getMessageValidation,
  getFeedbackValidation,
  getUpdateDisplayName,
  getCreateNewRoom,
  getCreateBreakoutRoom,
  getRenameBreakoutRoom,
  getBreakoutRoomSettings,
};
